/**
 * Lógica de apresentação compartilhada entre o sino (notificationBell) e a tela de notificações
 * (pages/app/notifications): ícone por tipo, tempo relativo e o "plano de ação" de cada notificação.
 * Só dados/funções — sem CSS. Cada consumidor aplica seu próprio styles[className].
 */

// Tela de dados consolidados acadêmicos (reflete a tabela Academic) filtrada por disciplinas SEM data
// de início/término (StartDate OU EndDate nulo). Substitui a lista estática montada no backend.
export const ACADEMIC_MISSING_DATES_URL =
  "/app/dados-consolidados-academicos?page=1" +
  "&filter0_field=StartDate&filter0_operator=empty&filter0_values=%5B%5D&filter0_groupId=0" +
  "&filter1_field=EndDate&filter1_operator=empty&filter1_values=%5B%5D&filter1_groupId=1&filter1_condition=OR"

// Tela de dados consolidados acadêmicos recortada por um arquivo importado (Academic.FileId =
// correlationId da importação). Usada para "ver os dados que entraram deste arquivo".
export function buildFileDataUrl(notification) {
  let data = notification?.data
  if (typeof data === "string") {
    try {
      data = JSON.parse(data)
    } catch {
      return null
    }
  }
  const fileId = data?.correlationId
  if (!fileId) {
    return null
  }
  const params = new URLSearchParams({ fileId })
  if (data.fileName) {
    params.set("fileName", data.fileName)
  }
  return `/app/dados-consolidados-academicos?${params.toString()}`
}

export const NOTIFICATION_TYPE_ICONS = {
  VALUE_MAPPING_REQUIRED: { emoji: "⚠️", className: "iconMapping" },
  FIELD_MAPPING_REQUIRED: { emoji: "⚠️", className: "iconMapping" },
  IMPORT_COMPLETED: { emoji: "✅", className: "iconSuccess" },
  IMPORT_ERROR: { emoji: "❌", className: "iconError" },
  IMPORT_WARNING: { emoji: "⚠️", className: "iconMapping" },
  INDICATOR_CRITICAL: { emoji: "🔻", className: "iconError" },
  PREDICTIVE_ANALYSIS_COMPLETED: { emoji: "📈", className: "iconSuccess" },
  DROPOUT_RISK_ANALYSIS_COMPLETED: { emoji: "🎓", className: "iconSuccess" }
}

export function getIconInfo(type) {
  return NOTIFICATION_TYPE_ICONS[type] || { emoji: "🔔", className: "iconDefault" }
}

export function formatTimeAgo(dateString) {
  const now = new Date()
  const date = new Date(dateString)
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return "agora"
  if (diffMin < 60) return `${diffMin}min atrás`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h atrás`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d atrás`
  return date.toLocaleDateString("pt-BR")
}

/**
 * Plano de ação por tipo de notificação: o que aconteceu exige algo do usuário? Traz um resumo
 * (item da lista), passos (detalhe) e a ação do botão. Tipos sem plano (ex.: IMPORT_COMPLETED) são
 * informativos e não aparecem na aba/tela "Ação necessária".
 */
const MAPPING_PLAN = {
  summary: "Concluir o mapeamento para a importação continuar",
  steps: [
    "Abra a tela de revisão pelo botão abaixo.",
    "Relacione as colunas/valores do arquivo com os campos do sistema.",
    "Confirme o envio — a importação retoma de onde parou, sem reenviar o arquivo."
  ],
  actionLabel: "Resolver mapeamento",
  actionKind: "mapping"
}

const NOTIFICATION_ACTION_PLANS = {
  INDICATOR_CRITICAL: () => ({
    summary: "Ver o indicador crítico no semáforo e tratar a ação recomendada",
    steps: [
      "Abra a Visão Geral pelo botão abaixo.",
      "Confira o indicador marcado como crítico e a variação contra a média dos 3 meses anteriores.",
      "Trate a ação recomendada correspondente na fila logo abaixo do semáforo."
    ],
    actionLabel: "Abrir Visão Geral",
    actionKind: "navigate",
    actionUrl: "/app/dashboard"
  }),
  VALUE_MAPPING_REQUIRED: () => MAPPING_PLAN,
  FIELD_MAPPING_REQUIRED: () => MAPPING_PLAN,
  IMPORT_ERROR: () => ({
    summary: "Corrigir o arquivo e importar novamente",
    steps: [
      "Veja nos detalhes desta notificação as colunas/linhas com problema.",
      "Ajuste o arquivo (ou exporte-o novamente do seu sistema acadêmico).",
      "Envie o arquivo corrigido em Importar dados."
    ],
    actionLabel: "Ir para importações",
    actionKind: "goToImports"
  }),
  IMPORT_WARNING: (notification) => {
    const title = notification.title || ""
    if (title.includes("Disciplinas sem data")) {
      return {
        summary: "Ver as disciplinas importadas sem data de início/término",
        steps: [
          "Abra a lista pelo botão abaixo (dados consolidados acadêmicos, filtrados por data de início/término vazia).",
          "Confira quais disciplinas estão sem data.",
          "Corrija DT_Inicio_Disciplina/DT_Termino_Disciplina no arquivo e reimporte para que voltem aos indicadores filtrados por período."
        ],
        actionLabel: "Ver disciplinas sem data",
        actionKind: "navigate",
        actionUrl: ACADEMIC_MISSING_DATES_URL
      }
    }
    if (title.includes("Período aplicado")) {
      const fileDataUrl = buildFileDataUrl(notification)
      if (fileDataUrl) {
        return {
          summary: "Conferir os dados importados e se o período aplicado corresponde ao real",
          steps: [
            "Abra os dados importados deste arquivo pelo botão abaixo.",
            "Confira se o período aplicado corresponde ao período letivo real.",
            "Se estiver errado, reimporte o arquivo com as colunas de início/término das disciplinas (DT_Inicio_Disciplina e DT_Termino_Disciplina)."
          ],
          actionLabel: "Ver dados importados",
          actionKind: "navigate",
          actionUrl: fileDataUrl
        }
      }
      // Notificações antigas sem correlationId no data: cai no fluxo genérico de importações.
      return {
        summary: "Conferir se o período aplicado corresponde ao período letivo real",
        steps: [
          "Confira o período aplicado na mensagem desta notificação.",
          "Se estiver correto, nenhuma ação é necessária.",
          "Se estiver errado, reimporte o arquivo com as colunas de início/término das disciplinas (DT_Inicio_Disciplina e DT_Termino_Disciplina)."
        ],
        actionLabel: "Ir para importações",
        actionKind: "goToImports"
      }
    }
    if (title.includes("Datas não identificadas")) {
      return {
        summary: "Ver os registros importados sem data (ficam fora dos filtros por período)",
        steps: [
          "Abra a lista pelo botão abaixo (dados consolidados acadêmicos, filtrados por data de início/término vazia).",
          "Para trazê-los aos indicadores filtrados por período, reimporte incluindo as colunas de datas (DT_Inicio_Disciplina e DT_Termino_Disciplina).",
          "Se o arquivo não tiver essas colunas, inclua o período no título ou no nome do arquivo (ex.: \"2026S1\" ou \"Período: 01/01/2026 a 30/06/2026\") para o sistema aplicá-lo automaticamente."
        ],
        actionLabel: "Ver disciplinas sem data",
        actionKind: "navigate",
        actionUrl: ACADEMIC_MISSING_DATES_URL
      }
    }
    return {
      summary: "Ler o aviso e verificar os dados importados",
      steps: [
        "Leia a mensagem desta notificação.",
        "Verifique na tela de importações se os dados entraram como esperado."
      ],
      actionLabel: "Ir para importações",
      actionKind: "goToImports"
    }
  }
}

export function getActionPlan(notification) {
  const resolver = NOTIFICATION_ACTION_PLANS[notification.type]
  return resolver ? resolver(notification) : null
}
